import { useState, useCallback } from 'react';
import { Popover, Button, Stack, Group, Text, Switch, ActionIcon, Box, Checkbox, Divider } from '@mantine/core';
import { Settings, Plus, Menu, X } from 'react-feather';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import classes from './ColumnSettingsPopover.module.css';

export interface ColumnSettingsPopoverProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  onAddCustomColumn?: () => void;
  onDeleteCustomColumn?: (columnId: string) => void;
  /** Custom trigger element. If not provided, uses default "Columns" button */
  trigger?: React.ReactNode;
  /** Override z-index for use inside overlays like Drawers */
  zIndex?: number;
}

interface SortableColumnItemProps {
  column: ColumnConfig;
  onToggle: (id: string) => void;
  onDelete?: (id: string) => void;
}

function SortableColumnItem({ column, onToggle, onDelete }: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} className={classes.columnItem}>
      <Group gap="xs" wrap="nowrap">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          className={classes.dragHandle}
          {...attributes}
          {...listeners}
        >
          <Menu size={14} />
        </ActionIcon>
        <Text size="sm" className={classes.columnLabel}>
          {column.label}
          {column.isCustomField && (
            <Text component="span" size="xs" c="dimmed" ml={4}>
              (custom)
            </Text>
          )}
        </Text>
        <Group gap={4} ml="auto" wrap="nowrap">
          <Switch
            checked={column.visible}
            onChange={() => onToggle(column.id)}
            size="xs"
          />
          {column.isCustomField && onDelete && (
            <ActionIcon
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => onDelete(column.id)}
              title="Delete custom column"
            >
              <X size={12} />
            </ActionIcon>
          )}
        </Group>
      </Group>
    </Box>
  );
}

export function ColumnSettingsPopover({
  columns,
  onColumnsChange,
  onAddCustomColumn,
  onDeleteCustomColumn,
  trigger,
  zIndex,
}: ColumnSettingsPopoverProps) {
  const [opened, setOpened] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Snapshot of default visible state taken at first open
  const [defaultVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(columns.map(c => [c.id, c.visible]))
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const allVisible = columns.every(c => c.visible);

  const handleToggle = useCallback(
    (id: string) => {
      const updated = columns.map((col) =>
        col.id === id ? { ...col, visible: !col.visible } : col
      );
      onColumnsChange(updated);
    },
    [columns, onColumnsChange]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        onColumnsChange(columns.map(col => ({ ...col, visible: true })));
      } else {
        // Reset to default visible state
        onColumnsChange(columns.map(col => ({
          ...col,
          visible: col.defaultVisible ?? defaultVisible[col.id] ?? col.visible,
        })));
      }
    },
    [columns, onColumnsChange, defaultVisible]
  );

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false);
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = columns.findIndex((col) => col.id === active.id);
        const newIndex = columns.findIndex((col) => col.id === over.id);
        const reordered = arrayMove(columns, oldIndex, newIndex);
        onColumnsChange(reordered);
      }
    },
    [columns, onColumnsChange]
  );

  const handleDragCancel = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <Popover
      opened={opened}
      onChange={(value) => {
        if (!isDragging) setOpened(value);
      }}
      position="bottom-end"
      withinPortal
      shadow="md"
      zIndex={zIndex}
    >
      <Popover.Target>
        {trigger ? (
          <Box onClick={() => setOpened((o) => !o)} style={{ cursor: 'pointer' }}>
            {trigger}
          </Box>
        ) : (
          <Button
            variant="outline"
            color="gray"
            bg="white"
            style={{ borderColor: '#E8E6E3' }}
            leftSection={<Settings size={16} />}
            onClick={() => setOpened((o) => !o)}
          >
            Columns
          </Button>
        )}
      </Popover.Target>

      <Popover.Dropdown className={classes.dropdown}>
        <Stack gap="xs">
          <Text size="sm" fw={600} c="dimmed">
            Column Visibility
          </Text>

          {/* Select All toggle */}
          <Box className={classes.columnItem}>
            <Group gap="xs" wrap="nowrap">
              <Box w={22} />
              <Text size="sm" fw={600} style={{ flex: 1 }}>
                Select All
              </Text>
              <Checkbox
                checked={allVisible}
                indeterminate={!allVisible && columns.some(c => c.visible)}
                onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                size="xs"
                ml="auto"
              />
            </Group>
          </Box>

          <Divider />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={columns.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={4} className={classes.columnList}>
                {columns.map((column) => (
                  <SortableColumnItem
                    key={column.id}
                    column={column}
                    onToggle={handleToggle}
                    onDelete={onDeleteCustomColumn}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          {onAddCustomColumn && (
            <>
              <Box className={classes.divider} />
              <Button
                variant="subtle"
                size="xs"
                leftSection={<Plus size={14} />}
                onClick={() => {
                  setOpened(false);
                  onAddCustomColumn();
                }}
                fullWidth
              >
                Add Custom Column
              </Button>
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export default ColumnSettingsPopover;
